import { z } from 'zod';
import { paginationSchema, text } from './common.js';
export const facilitySchema = z.object({
    id: z.number().int(),
    bu: z.string(),
    name: z.string(),
    domain: z.string().nullable(),
    address: z.string().nullable(),
    phone: z.string().nullable(),
    clusterId: z.number().int().nullable(),
    cluster: z.string().nullable(),
    adminUserIds: z.array(z.number().int()),
    admins: z.array(z.object({ id: z.number().int(), name: z.string() })),
    isActive: z.boolean(),
    /** Managed manually: the Entra reconcile reports but never changes this facility's admins. */
    adminSyncLocked: z.boolean(),
});
const hostname = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(?=.{1,190}$)([a-z0-9-]+\.)+[a-z]{2,}$/, 'must be a hostname like facility.com');
export const facilityCreateSchema = z.object({
    bu: text(1, 10),
    name: text(2, 150),
    domain: hostname.nullable().optional(),
    address: text(0, 255).nullable().optional(),
    phone: text(0, 30).nullable().optional(),
    clusterId: z.number().int().positive().nullable().optional(),
    adminUserIds: z.array(z.number().int().positive()).max(20).optional(),
    isActive: z.boolean().optional(),
});
/** T45: one duplicate pair (alias-address account vs directory-UPN account) and what a merge would do. */
const mergeSideSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    email: z.string(),
    activity: z.number().int(),
});
export const adminSyncMergeItemSchema = z.object({
    alias: z.string(),
    upn: z.string(),
    legacy: mergeSideSchema,
    created: mergeSideSchema,
    action: z.enum(['keep_legacy', 'keep_created', 'retire_created', 'skip']),
    reason: z.string(),
});
export const adminSyncMergeRequestSchema = z.object({
    /** Aliases the caller reviewed in the preview; the server recomputes the plan for them. */
    aliases: z.array(z.string().trim().toLowerCase().max(190)).min(1).max(200),
});
export const adminSyncMergeResultSchema = z.object({
    merged: z.number().int(),
    skipped: z.number().int(),
    failed: z.array(z.object({ alias: z.string(), message: z.string() })),
});
/** T43: "managed manually" is set through its own endpoint, gated by admin_sync.manage. */
export const adminSyncLockSchema = z.object({ locked: z.boolean() });
/** Outcome of one alias lookup for one facility (spec §5.15). */
export const ADMIN_SYNC_STATUSES = [
    'linked',
    'created',
    'proposed',
    'locked',
    'not_found',
    'ambiguous',
    'disabled',
    'error',
    'vacant',
];
export const adminSyncRowSchema = z.object({
    id: z.number().int(),
    facilityId: z.number().int(),
    facility: z.string(),
    bu: z.string(),
    domain: z.string().nullable(),
    adminSyncLocked: z.boolean(),
    role: z.enum(['Administrator', 'Delegate']),
    alias: z.string(),
    status: z.enum(ADMIN_SYNC_STATUSES),
    entraId: z.string().nullable(),
    upn: z.string().nullable(),
    displayName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    userId: z.number().int().nullable(),
    message: z.string().nullable(),
    checkedAt: z.string(),
    appliedAt: z.string().nullable(),
    /** Who is linked to the facility right now, for the side-by-side view. */
    currentAdmins: z.array(z.object({ id: z.number().int(), name: z.string(), role: z.string(), source: z.string() })),
});
export const adminSyncReportSchema = z.object({
    facilities: z.number().int(),
    lookups: z.number().int(),
    linked: z.number().int(),
    created: z.number().int(),
    unlinked: z.number().int(),
    proposed: z.number().int(),
    notFound: z.number().int(),
    vacant: z.number().int(),
    ambiguous: z.number().int(),
    errors: z.number().int(),
});
export const facilityEditSchema = facilityCreateSchema.partial();
export const facilityListQuerySchema = paginationSchema.extend({
    q: z.string().trim().max(150).optional(),
    clusterId: z.coerce.number().int().positive().optional(),
    active: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const clusterSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    leaderUserId: z.number().int().nullable(),
    leader: z.string().nullable(),
    facilityIds: z.array(z.number().int()),
    facilityCount: z.number().int(),
});
export const clusterCreateSchema = z.object({
    name: text(2, 120),
    leaderUserId: z.number().int().positive().nullable().optional(),
    facilityIds: z.array(z.number().int().positive()).max(200).optional(),
});
export const clusterEditSchema = clusterCreateSchema.partial();
//# sourceMappingURL=facilities.js.map